import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronDownIcon } from './icons';

interface CustomDropdownProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  error = false,
  disabled = false,
  onBlur,
  onFocus
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    onBlur?.();
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        onFocus?.();
      } else {
        onBlur?.();
      }
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={toggleDropdown}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-lg transition-all duration-300 text-left flex items-center justify-between cursor-pointer"
        style={{
          backgroundColor: colors.background.default,
          border: `1px solid ${error ? colors.error : isOpen ? colors.accent.mint : colors.border}`,
          color: selectedOption ? colors.text.primary : colors.text.secondary,
          opacity: disabled ? 0.6 : 1,
          boxShadow: isOpen ? `0 0 0 3px ${colors.accent.mint}20` : 'none'
        }}
        onMouseEnter={(e) => {
          if (!disabled && !error) {
            e.currentTarget.style.borderColor = colors.accent.mint;
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = error ? colors.error : colors.border;
          }
        }}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDownIcon 
          className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          style={{ color: colors.accent.mint }}
        />
      </button>

      {isOpen && !disabled && (
        <div
          className="absolute z-50 w-full mt-1 rounded-lg shadow-lg overflow-hidden"
          style={{
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.accent.mint}`,
            boxShadow: `0 10px 30px -10px ${colors.accent.mint}30`
          }}
        >
          <div className="max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className="w-full px-4 py-3 text-left transition-all duration-200 hover:scale-[1.01]"
                style={{
                  backgroundColor: value === option.value ? `${colors.accent.mint}20` : 'transparent',
                  color: value === option.value ? colors.accent.mint : colors.text.primary,
                  borderLeft: value === option.value ? `3px solid ${colors.accent.mint}` : '3px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.accent.mint}10`;
                  if (value !== option.value) {
                    e.currentTarget.style.color = colors.accent.mint;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = value === option.value ? `${colors.accent.mint}20` : 'transparent';
                  e.currentTarget.style.color = value === option.value ? colors.accent.mint : colors.text.primary;
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;